/**
 * Play-mode gate and draw ordering for `TRACE_materials_occlusion` materials.
 *
 * Depth-only rendering is a play-mode / AR behaviour. In the editor an
 * occluder shows its PBR fallback (black) so it is visible and selectable;
 * entering play mode or an XR session switches every marked material in the
 * scene to depth-only, and leaving both switches it back. `TraceScene` owns
 * one instance and feeds it the two inputs.
 *
 * Two mechanisms:
 *
 * - **Colour write.** `disableColorWrite` is flipped on every marked material
 *   in `scene.materials` whenever the mode changes, and on every material that
 *   joins the scene afterwards (a state-driven `src` swap clones a fresh
 *   occluder mid-play; a clone made in edit mode may copy a stale flag from
 *   its source). Babylon notifies `onNewMaterialAddedObservable` through
 *   `SetImmediate`, a macrotask after the constructor, so by the time the
 *   join hook runs `Material.clone` has copied the metadata that carries the
 *   marker and the hook can apply directly. A material the loader marks
 *   *after* it joined (direct `ImportMeshAsync` loads; the marker lands after
 *   the base PBR properties resolve) is caught through
 *   `onOccluderMarkedObservable` instead.
 *
 * - **Draw order.** An occluder that draws after an opaque mesh writes depth
 *   but cannot erase colour already in the framebuffer, so occluders must
 *   render first (Unity's `Geometry-1`; RealityKit does it internally). The
 *   opaque sort for rendering group 0 puts occluder sub-meshes first and
 *   falls back to Babylon's default painter order for everything else, so
 *   the state-change batching that order gives is kept.
 */
import type { Scene } from "@babylonjs/core/scene";
import type { Material } from "@babylonjs/core/Materials/material";
import type { SubMesh } from "@babylonjs/core/Meshes/subMesh";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { Nullable } from "@babylonjs/core/types";
import { RenderingGroup } from "@babylonjs/core/Rendering/renderingGroup";
import { isOccluderMaterial, onOccluderMarkedObservable } from "../loaders/gltf/TRACE_materials_occlusion";

/**
 * Opaque sort: occluder sub-meshes first, then Babylon's default painter
 * order (material id) so the non-occluder part of the queue is unchanged.
 */
export function occluderFirstSortCompare(a: SubMesh, b: SubMesh): number {
    const rankA = isOccluderMaterial(a.getMaterial()) ? 0 : 1;
    const rankB = isOccluderMaterial(b.getMaterial()) ? 0 : 1;
    if (rankA !== rankB) return rankA - rankB;
    return RenderingGroup.PainterSortCompare(a, b);
}

export class OccluderMode {
    private readonly _scene: Scene;
    private _playing = false;
    private _xrActive = false;
    private _depthOnly = false;
    private _newMaterialObserver: Nullable<Observer<Material>> = null;
    private _markedObserver: Nullable<Observer<Material>> = null;

    constructor(scene: Scene) {
        this._scene = scene;
        scene.setRenderingOrder(0, occluderFirstSortCompare);
        // Applied in edit mode too: a clone copies its source's flag, and a
        // container source that was flagged during play is never rescanned.
        this._newMaterialObserver = scene.onNewMaterialAddedObservable.add((material) => this._apply(material));
        this._markedObserver = onOccluderMarkedObservable.add((material) => {
            // Only materials this scene renders: a container's source material
            // is never drawn, and flagging it would leak the flag into clones.
            if (material.getScene() === scene && scene.materials.includes(material)) this._apply(material);
        });
    }

    /** Effective state: true while play mode or an XR session is active. */
    public get depthOnly(): boolean {
        return this._depthOnly;
    }

    public setPlaying(playing: boolean): void {
        this._playing = playing;
        this._recompute();
    }

    public setXRActive(active: boolean): void {
        this._xrActive = active;
        this._recompute();
    }

    public dispose(): void {
        if (this._newMaterialObserver) {
            this._scene.onNewMaterialAddedObservable.remove(this._newMaterialObserver);
            this._newMaterialObserver = null;
        }
        if (this._markedObserver) {
            onOccluderMarkedObservable.remove(this._markedObserver);
            this._markedObserver = null;
        }
    }

    private _recompute(): void {
        const next = this._playing || this._xrActive;
        if (next === this._depthOnly) return;
        this._depthOnly = next;
        for (const material of this._scene.materials) {
            this._apply(material);
        }
    }

    private _apply(material: Material): void {
        if (!isOccluderMaterial(material)) return;
        if (material.disableColorWrite !== this._depthOnly) {
            material.disableColorWrite = this._depthOnly;
        }
    }
}
